"use client";

import { useEmailValidation } from "@/components/hooks/useEmailValidation";
import { usePasswordValidation } from "@/components/hooks/usePasswordValidation";
import { api } from "@/lib/api";
import axios from "axios";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import type { TipoIdentificacion } from "../../Types/types";

export function useRegisterForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---- estado de error para mostrar al usuario ----
  const [apiErrorUser, setApiErrorUser] = useState<string | null>(null);

  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successData, setSuccessData] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [numeroDocumentoError, setNumeroDocumentoError] = useState("");
  const [celular, setCelular] = useState("");
  const [celularError, setCelularError] = useState("");
  const { emailError, handleEmailChange } = useEmailValidation();
  const {
    passwordError,
    confirmPasswordError,
    handlePasswordChange,
    handleConfirmPasswordChange,
  } = usePasswordValidation();
  const [primer_nombre, setPrimerNombre] = useState("");
  const [segundo_nombre, setSegundoNombre] = useState("");
  const [primer_apellido, setPrimerApellido] = useState("");
  const [segundo_apellido, setSegundoApellido] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const router = useRouter();

  // ---- Estados para tipos de identificación ----
  const [tiposIdentificacion, setTiposIdentificacion] = useState<
    TipoIdentificacion[]
  >([]);
  const [loadingTipos, setLoadingTipos] = useState(true);

  // Handler tipo documento
  const handleTipoDocumentoChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setTipoDocumento(e.target.value);
    // resetear error cuando elige tipo
    if (numeroDocumentoError) setNumeroDocumentoError("");
  };
  // Handler número documento
  const handleNumeroDocumentoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setNumeroDocumento(value);

    if (!/^\d*$/.test(value)) {
      setNumeroDocumentoError(
        "El número de documento solo puede contener dígitos"
      );
    } else {
      setNumeroDocumentoError("");
    }
  };

  // Handlers correo
  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    handleEmailChange(value);
  };

  // Handler número celular
  const handleTelefonoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCelular(value);

    if (!/^\d*$/.test(value)) {
      setCelularError("El número de documento solo puede contener dígitos");
    } else {
      setCelularError("");
    }
  };

  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setPassword(value);
    handlePasswordChange(value);
    // Si ya hay algo en confirmPassword, revalidar la coincidencia
    if (confirmPassword) {
      handleConfirmPasswordChange(value, confirmPassword);
    }
  };

  const handleConfirmPasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    handleConfirmPasswordChange(password, value);
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    setAcceptTerms(checked);
  };

  const handlePrimerNombreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrimerNombre(e.target.value);
  };
  const handleSegundoNombreChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSegundoNombre(e.target.value);
  };
  const handlePrimerApellidoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPrimerApellido(e.target.value);
  };
  const handleSegundoApellidoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSegundoApellido(e.target.value);
  };

  // Handler para reCAPTCHA
  const handleRecaptchaVerify = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken(null);
    setApiErrorUser(
      "Error al verificar reCAPTCHA. Por favor intenta de nuevo."
    );
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setApiErrorUser("reCAPTCHA expirado. Por favor verifica nuevamente.");
  };

  // Handler para cerrar el alert de error
  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
    setApiErrorUser(null);
  };

  // Handler para cerrar el alert de éxito
  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
    setSuccessData(null);
  };

  // Verificar si el captcha está activo
  const isCaptchaActive = process.env.NEXT_PUBLIC_CAPTCHA_IS_ACTIVE !== "false";

  // Verificar si todos los campos son válidos
  const areFieldsValid =
    primer_nombre.trim() !== "" &&
    primer_apellido.trim() !== "" &&
    tipoDocumento.trim() !== "" &&
    numeroDocumento.trim() !== "" &&
    !numeroDocumentoError &&
    email.trim() !== "" &&
    !emailError &&
    celular.trim() !== "" &&
    password.trim() !== "" &&
    !passwordError &&
    confirmPassword.trim() !== "" &&
    !confirmPasswordError;

  const areAllFieldsValid =
    areFieldsValid &&
    acceptTerms &&
    (isCaptchaActive ? recaptchaToken !== null : true);

  // Función central para mapear errores de la API a mensajes de usuario
  const mapApiErrorToMessage = (status?: number, payload?: unknown) => {
    const str = payload ? String(payload).toLowerCase() : "";

    // Errores por código HTTP
    if (
      status === 400 ||
      str.includes("validation") ||
      str.includes("validación")
    ) {
      return "Los datos ingresados no son válidos. Por favor revísalos e intenta de nuevo.";
    }
    if (
      status === 409 ||
      str.includes("already exists") ||
      str.includes("ya existe") ||
      str.includes("duplicate")
    ) {
      return "Ya existe una cuenta con este correo o documento. Intenta iniciar sesión.";
    }
    if (status && status >= 500) {
      return "Error en el servidor. Intenta más tarde.";
    }

    // Errores de validación: puede venir como array u objeto
    if (Array.isArray(payload)) {
      // si la API retorna un array de mensajes
      return payload.slice(0, 3).join(", ");
    }
    if (payload && typeof payload === "object" && payload !== null) {
      const errorObj = payload as Record<string, unknown>;

      // Si viene { error: "..." } o { message: "..." } o { errors: [...] }
      if (errorObj.error && typeof errorObj.error === "string") {
        return errorObj.error;
      }
      if (errorObj.message && typeof errorObj.message === "string") {
        return errorObj.message;
      }
      if (Array.isArray(errorObj.errors) && errorObj.errors.length) {
        // devuelve los primeros 2 mensajes concatenados
        return errorObj.errors
          .slice(0, 2)
          .map((it) => String(it))
          .join(" - ");
      }
    }

    // fallback / genérico
    return "Error al registrar usuario. Verifica tus datos e intenta nuevamente.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones simples
    const isP_NombreValid = primer_nombre.trim() !== "";
    const isP_ApellidoValid = primer_apellido.trim() !== "";
    const isT_DocValid = tipoDocumento.trim() !== "";
    const isNum_DocValid = /^\d+$/.test(numeroDocumento);
    const isEmailValid = !emailError && email.includes("@");
    const isCelularValid = /^\d+$/.test(celular);
    const isPasswordValid = !passwordError;
    const isConfirmPasswordValid = !confirmPasswordError;

    if (
      !isP_NombreValid ||
      !isP_ApellidoValid ||
      !isT_DocValid ||
      !isNum_DocValid ||
      !isEmailValid ||
      !isCelularValid ||
      !isPasswordValid ||
      !isConfirmPasswordValid ||
      !acceptTerms ||
      (isCaptchaActive && !recaptchaToken)
    ) {
      const missingCaptcha = isCaptchaActive && !recaptchaToken;
      setApiErrorUser(
        missingCaptcha
          ? "Por favor completa todos los campos y verifica el reCAPTCHA."
          : "Por favor completa todos los campos requeridos."
      );
      return;
    }

    try {
      setLoading(true);
      // limpiar errores previos
      setApiErrorUser(null);

      // petición axios
      const { data } = await api.post("/api/auth/register", {
        primer_nombre,
        segundo_nombre,
        primer_apellido,
        segundo_apellido,
        tipo_identificacion: tipoDocumento,
        numero_identificacion: numeroDocumento,
        correo: email,
        tel_celular: celular,
        clave: password,
        password_confirmation: confirmPassword,
        recaptcha_token: recaptchaToken,
      });

      if (data.success) {
        // Mostrar alert de éxito
        setSuccessData({
          title: "¡Registro exitoso!",
          message: `Usuario registrado correctamente. Serás redirigido al login para iniciar sesión.`,
        });
        setShowSuccessAlert(true);

        // 🔹 Resetear formulario
        setPrimerNombre("");
        setSegundoNombre("");
        setPrimerApellido("");
        setSegundoApellido("");
        setTipoDocumento("");
        setNumeroDocumento("");
        setCelular("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setAcceptTerms(false);
        setRecaptchaToken(null);

        // 🔹 Pasar al login con un breve delay para mostrar éxito
        setTimeout(() => {
          router.push("/login");
        }, 3000);
      } else {
        const friendly = mapApiErrorToMessage(undefined, data);
        setApiErrorUser(friendly);
      }
    } catch (err) {
      // calculamos mensaje para el usuario
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const payload = err.response?.data ?? err.message ?? err.toString();

        // Mapear a mensaje amigable y mostrarlo
        const friendly = mapApiErrorToMessage(status, payload);
        setApiErrorUser(friendly);
      } else {
        // error no-axios (ej. runtime)
        setApiErrorUser("Error del servidor. Intenta de nuevo más tarde.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Cargar tipos de identificación al montar el componente
  useEffect(() => {
    const fetchTiposIdentificacion = async () => {
      try {
        setLoadingTipos(true);
        const { data } = await api.get(
          "/api/dashboards/mis_datos/selects/tipos_identificacion"
        );

        if (data && data.data && Array.isArray(data.data)) {
          setTiposIdentificacion(data.data);
        } else if (data && Array.isArray(data)) {
          setTiposIdentificacion(data);
        } else {
          console.error("Formato de datos inesperado:", data);
        }
      } catch (error) {
        console.error("Error cargando tipos de identificación:", error);
        if (axios.isAxiosError(error)) {
          console.error("Status:", error.response?.status);
          console.error("Data:", error.response?.data);
        }
      } finally {
        setLoadingTipos(false);
      }
    };

    fetchTiposIdentificacion();
  }, []);

  // Mostrar alert cuando hay error de API (amigable)
  useEffect(() => {
    if (apiErrorUser) {
      setShowErrorAlert(true);
    }
  }, [apiErrorUser]);

  return {
    // Estado del formulario
    email,
    password,
    confirmPassword,
    emailError,
    passwordError,
    confirmPasswordError,
    acceptTerms,
    loading,
    tipoDocumento,
    numeroDocumento,
    numeroDocumentoError,
    celular,
    celularError,
    primer_nombre,
    segundo_nombre,
    primer_apellido,
    segundo_apellido,
    tiposIdentificacion,
    loadingTipos,
    areFieldsValid,
    areAllFieldsValid,

    // Alerts
    apiErrorUser,
    showErrorAlert,
    showSuccessAlert,
    successData,

    // Handlers
    handleEmailInputChange,
    handlePasswordInputChange,
    handleConfirmPasswordInputChange,
    handleSubmit,
    handleAcceptTermsChange,
    handleTipoDocumentoChange,
    handleNumeroDocumentoChange,
    handleTelefonoChange,
    handleRecaptchaVerify,
    handleRecaptchaError,
    handleRecaptchaExpired,
    handlePrimerNombreChange,
    handleSegundoNombreChange,
    handlePrimerApellidoChange,
    handleSegundoApellidoChange,
    handleCloseErrorAlert,
    handleCloseSuccessAlert,
  };
}
